import React, { useState } from "react";
import {
  FaCar,
  FaHospital,
  FaShoppingCart,
  FaTheaterMasks,
  FaPhoneAlt,
  FaUserMd,
  FaWheelchair,
  FaClock,
  FaCheckCircle,
  FaClipboardList,
  FaShieldAlt,
  FaHandHoldingHeart,
  FaChevronDown,
  FaEnvelope,
  FaArrowDown,
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useColorMode, Box } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const TransportPMR = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const examples = [
    {
      title: "Rendez-vous médicaux",
      subtitle: "Santé & Suivi",
      icon: FaHospital,
      description: "Nous vous transportons à vos rendez-vous dans des véhicules adaptés, avec des chauffeurs sensibilisés à vos besoins spécifiques.",
      list: ["Attente sur place", "Aide à l'accueil", "Respect strict des horaires"]
    },
    {
      title: "Courses & Sorties",
      subtitle: "Vie quotidienne",
      icon: FaShoppingCart,
      description: "Profitez de nos services pour vos courses, visites culturelles ou activités sociales. Nos chauffeurs assurent votre confort tout au long du trajet.",
      list: ["Aide au port des achats", "Accompagnement au bras", "Véhicules spacieux"]
    },
    {
      title: "Trajets longue distance",
      subtitle: "Voyages adaptés",
      icon: FaCar,
      description: "Besoin de vous déplacer en dehors de votre région ? Nous proposons des trajets longue distance confortables et adaptés.",
      list: ["Confort optimal", "Pauses régulières", "Organisation sur-mesure"]
    },
    {
      title: "Activités de loisirs",
      subtitle: "Épanouissement",
      icon: FaTheaterMasks,
      description: "Ne laissez aucune contrainte limiter vos loisirs. Nos chauffeurs vous accompagnent pour vos événements, en toute sérénité.",
      list: ["Adaptabilité aux horaires", "Trajets discrets", "Prise en charge porte-à-porte"]
    },
  ];

  const timelineSteps = [
    { step: "01", title: "Prise de rendez-vous", desc: "Contactez-nous par téléphone ou via notre site pour exprimer votre besoin, l'adresse et les spécificités de votre déplacement.", icon: FaPhoneAlt },
    { step: "02", title: "Planification sur-mesure", desc: "Nous sélectionnons le véhicule PMR adapté et planifions l'horaire précis avec notre chauffeur-accompagnateur.", icon: FaClipboardList },
    { step: "03", title: "Prise en charge à domicile", desc: "Le chauffeur se présente à votre domicile à l'heure convenue et vous aide à vous installer confortablement et en toute sécurité.", icon: FaWheelchair },
    { step: "04", title: "Trajet & Accompagnement", desc: "Un transport serein jusqu'à votre destination, incluant une aide sur place (installation, formalités) si vous le souhaitez.", icon: FaHandHoldingHeart }
  ];

  const faqs = [
    {
      question: "Un proche peut-il m'accompagner pendant le trajet ?",
      answer: "Absolument. Un accompagnant (membre de la famille ou aidant) peut voyager avec vous sans aucun surcoût. Nos véhicules sont suffisamment spacieux pour vous accueillir confortablement."
    },
    {
      question: "Le chauffeur m'accompagne-t-il jusqu'à la salle d'attente ?",
      answer: "Oui, notre service est du « porte-à-porte » au sens strict. Le chauffeur-accompagnateur ne se contente pas de vous déposer sur le trottoir ; il vous accompagne jusqu'à l'accueil du secrétariat ou dans la salle d'attente de votre médecin."
    },
    {
      question: "Faut-il prévoir son propre fauteuil roulant ?",
      answer: "C'est préférable si vous possédez un fauteuil adapté à votre morphologie. Toutefois, en cas de besoin spécifique ou si vous n'en possédez pas, nous pouvons mettre un fauteuil roulant de transfert à votre disposition sur simple demande préalable."
    },
    {
      question: "Que se passe-t-il si mon rendez-vous médical s'éternise ?",
      answer: "Lors de la réservation, vous avez la possibilité de choisir l'option « Attente sur place ». Le chauffeur restera à votre disposition le temps nécessaire pour s'assurer que vous puissiez repartir dès la fin de votre consultation."
    }
  ];

  return (
    <Box position="relative" overflow="hidden" className={isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}>
      <Helmet>
        <title>Transport PMR — Personnes à mobilité réduite | Hetep Iaout Services</title>
        <meta name="description" content="Transport de personnes à mobilité réduite (PMR) en Île-de-France. Véhicules adaptés, chauffeurs formés. Réservez votre transport médical ou de loisirs avec HIS." />
        <link rel="canonical" href="https://www.hisvie.com/transport-de-pmr/" />
      </Helmet>

      <div className="w-full">

        {/* ── HERO ── */}
        <section className="relative w-full min-h-[90vh] pt-32 pb-8 px-6 max-w-5xl mx-auto flex flex-col items-center justify-between text-center">

          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none -z-10"></div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col items-center w-full flex-grow justify-center"
          >
            <h1 className={`text-4xl sm:text-5xl md:text-7xl font-black mb-6 md:mb-8 leading-[1.05] tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
              Votre mobilité,<br className="hidden md:block"/>
              <span className="text-blue-600 font-serif italic font-light">sans aucune limite.</span>
            </h1>

            <p className={`text-base md:text-xl leading-relaxed mb-8 md:mb-10 max-w-3xl mx-auto font-light ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Un accompagnement humain et des véhicules homologués pour vous garantir confort, sécurité et liberté — de votre domicile jusqu'à destination.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10 md:mb-12">
              <Link to="/demande-de-devis" className="w-full sm:w-auto">
                <button className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-2xl shadow-xl shadow-blue-600/30 transition-all text-base flex items-center justify-center gap-2">
                  <FaClipboardList size={16} /> Demander une prise en charge
                </button>
              </Link>
              <Link to="/contact" className="w-full sm:w-auto">
                <button className={`w-full font-bold py-4 px-8 rounded-2xl transition-all border text-base flex items-center justify-center gap-2 ${isDark ? 'border-gray-700 text-white bg-white/5 hover:bg-white/10' : 'border-gray-200 text-[#1B365D] bg-white hover:bg-gray-50 shadow-sm'}`}>
                  <FaPhoneAlt size={14} /> Appeler un conseiller
                </button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            className={`mt-10 md:mt-auto pt-6 flex flex-col items-center gap-2 opacity-60 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          >
            <span className="text-[10px] font-bold tracking-widest uppercase">Découvrir les garanties</span>
            <FaArrowDown size={14} />
          </motion.div>

        </section>

        {/* ── BENTO BOX : GARANTIES ── */}
        <motion.section
          initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
          className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24"
        >
          <div className="text-center mb-12 md:mb-16">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5 ${isDark ? 'bg-blue-900/30 border-blue-800/50 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
              <span className="text-[10px] font-bold tracking-widest uppercase">Notre protocole de prise en charge</span>
            </div>
            <h2 className={`text-3xl md:text-5xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
              Une équipe soignante dédiée<br />
              <span className="text-blue-600 font-light italic font-serif">à votre mobilité</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`md:col-span-4 relative overflow-hidden rounded-[2rem] md:rounded-[3rem] p-8 md:p-12 flex flex-col justify-end min-h-[300px] border ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-800' : 'bg-gradient-to-br from-[#1B365D] to-blue-900 border-blue-900 shadow-xl'}`}
            >
              <FaUserMd className={`absolute right-10 bottom-10 opacity-10 ${isDark ? 'text-white' : 'text-white'}`} size={140} />
              <div className="relative z-10">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md ${isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-white/20 text-white'}`}>
                  <FaUserMd size={24} />
                </div>
                <h3 className="text-2xl md:text-4xl font-black text-white mb-4 leading-snug">
                  Chauffeurs-accompagnateurs<br />formés aux soins
                </h3>
                <p className={`text-sm md:text-base leading-relaxed max-w-lg font-light ${isDark ? 'text-gray-300' : 'text-blue-100'}`}>
                  Diplômés en assistance aux personnes dépendantes, certifiés PSC1, sensibilisés aux pathologies lourdes (Parkinson, AVC, handicap moteur).
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
              className={`md:col-span-2 p-8 rounded-[2rem] md:rounded-[3rem] flex flex-col justify-between border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white shadow-xl shadow-blue-900/5 border-gray-100'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                <FaWheelchair size={20} />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-widest uppercase text-blue-600 mb-2">Flotte homologuée</div>
                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Véhicules PMR</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Rampes d'accès, systèmes d'ancrage certifiés et sanitisation régulière.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }}
              className={`md:col-span-3 p-8 rounded-[2rem] md:rounded-[3rem] flex flex-col justify-between border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white shadow-xl shadow-blue-900/5 border-gray-100'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                <FaClock size={20} />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-widest uppercase text-blue-600 mb-2">Protocole horaire strict</div>
                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Ponctualité médicale</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Chaque rendez-vous est planifié avec des marges de sécurité. Aucun retard toléré.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.35 }}
              className={`md:col-span-3 p-8 rounded-[2rem] md:rounded-[3rem] flex flex-col justify-between border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-blue-50 border-blue-100'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-gray-800 text-yellow-400' : 'bg-[#1B365D] text-yellow-400'}`}>
                <FaShieldAlt size={20} />
              </div>
              <div>
                <div className={`text-[10px] font-bold tracking-widest uppercase mb-2 ${isDark ? 'text-yellow-400' : 'text-blue-600'}`}>Conformité</div>
                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Service agréé & assuré</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Couverture RC Professionnelle spécifique au transport de personnes à mobilité réduite.
                </p>
              </div>
            </motion.div>

          </div>
        </motion.section>

        {/* ── CAS D'USAGE ── */}
        <motion.section
          initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
          className={`py-16 md:py-24 border-t ${isDark ? 'border-gray-800 bg-[#050914]' : 'border-gray-100 bg-[#FAFCFF]'}`}
        >
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className={`text-3xl md:text-5xl font-black mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Quels déplacements<br />
                <span className="text-blue-600 font-light italic font-serif">prenons-nous en charge ?</span>
              </h2>
              <div className="w-16 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {examples.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                  className={`relative overflow-hidden rounded-3xl md:rounded-[2rem] border transition-all hover:shadow-xl hover:-translate-y-1 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}
                >
                  <div className={`absolute -right-6 -bottom-6 opacity-[0.03] pointer-events-none ${isDark ? 'text-white' : 'text-blue-900'}`}>
                    <service.icon size={200} />
                  </div>

                  <div className="p-8 md:p-10 relative z-10">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <span className="text-[10px] font-bold tracking-widest uppercase text-blue-600 block mb-2">{service.subtitle}</span>
                        <h3 className={`text-2xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{service.title}</h3>
                      </div>
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                        <service.icon size={24} />
                      </div>
                    </div>

                    <p className={`text-base md:text-lg leading-relaxed mb-8 font-light ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {service.description}
                    </p>

                    <div className={`border-t pt-6 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                      <div className="grid grid-cols-1 gap-3">
                        {service.list.map((item, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <FaCheckCircle className="text-blue-600 shrink-0" size={14} />
                            <span className={`text-sm md:text-base font-medium ${isDark ? 'text-gray-300' : 'text-[#1B365D]'}`}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ── TIMELINE VERTICALE ── */}
        <motion.div
          className="py-16 md:py-32 px-4 md:px-6 max-w-4xl mx-auto"
          initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
        >
          <div className="text-center mb-16 md:mb-24">
            <h2 className={`text-3xl md:text-5xl font-black mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
              Un accompagnement <span className="text-blue-600 font-light italic font-serif">fluide.</span>
            </h2>
            <p className={`text-lg md:text-xl font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Notre protocole de prise en charge, étape par étape.</p>
          </div>

          <div className="relative">
            <div className={`absolute left-[27px] md:left-1/2 top-0 bottom-0 w-[2px] md:-translate-x-1/2 ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}></div>

            {timelineSteps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row items-start md:items-center justify-between mb-12 md:mb-16 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className="hidden md:block md:w-5/12"></div>

                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-14 h-14 rounded-full bg-blue-600 border-4 border-white dark:border-[#0a0f16] flex items-center justify-center text-white shrink-0 z-10 shadow-lg shadow-blue-900/30">
                  <item.icon size={16} />
                </div>

                <div className={`w-full md:w-5/12 pl-20 md:pl-0 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-blue-600 block mb-2">Étape {item.step}</span>
                  <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{item.title}</h3>
                  <p className={`text-sm md:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ── FAQ ── */}
        <motion.section
          initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
          className={`py-16 md:py-24 border-t ${isDark ? 'border-gray-800 bg-[#050914]' : 'border-gray-100 bg-[#FAFCFF]'}`}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <h2 className={`text-3xl md:text-5xl font-black mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Questions <span className="text-blue-600 font-light italic font-serif">fréquentes.</span>
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white hover:border-blue-300'}`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-6 text-left flex items-center justify-between focus:outline-none"
                  >
                    <span className={`text-lg font-bold pr-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                      {faq.question}
                    </span>
                    <motion.div
                      animate={{ rotate: openFaq === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}
                    >
                      <FaChevronDown size={14} />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className={`px-6 pb-6 pt-0 text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ── CTA FINAL ── */}
        <motion.section
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
          className="max-w-4xl mx-auto px-4 pb-20"
        >
          <div className={`relative overflow-hidden rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 border shadow-xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <div className="flex-1 z-10 text-center md:text-left">
              <h2 className={`text-2xl md:text-4xl font-black mb-3 tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Planifiez votre <span className="text-blue-600 font-serif italic font-light">prochain déplacement</span>.
              </h2>
              <p className={`text-sm md:text-base max-w-md mx-auto md:mx-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Nos conseillers sont à votre écoute pour organiser votre transport adapté sur-mesure.</p>
            </div>

            <div className="flex flex-col sm:flex-row w-full md:w-auto gap-3 z-10 shrink-0">
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3.5 rounded-xl font-bold hover:bg-blue-700 transition-all w-full shadow-md text-sm md:text-base">
                  <FaPhoneAlt size={14} /> Nous contacter
                </button>
              </Link>
              <a href="mailto:his@donnerlavieauxannees.com" className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold transition-all border w-full sm:w-auto text-sm md:text-base ${isDark ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'}`}>
                <FaEnvelope size={14} /> Email
              </a>
            </div>
          </div>
        </motion.section>

      </div>
    </Box>
  );
};

export default TransportPMR;